/**
 * SearchField — a debounced search box.
 *
 * Used by: the product list (`GET /products?search=`), the permission matrix
 * filter, the branch picker, the translation queue and the audit log viewer.
 *
 * It keeps the typed text in local state and only calls `onSearch` after the
 * debounce, so a screen can bind `onSearch` straight to a query key without
 * firing a request per keystroke. `value` remains available for the rare caller
 * that wants full control.
 *
 * Accessibility: `type="search"` + `role="searchbox"` semantics come free from
 * the input; the clear button has a label; Escape clears (a convention users
 * already know from browsers).
 */
import { useEffect, useRef, useState } from 'react';
import { cn } from '../utils.js';
import { Input } from './Input.js';
import { IconButton } from './IconButton.js';
import { CloseGlyph } from './Modal.js';

export interface SearchFieldProps {
  readonly placeholder?: string;
  /** Fired after the debounce with the trimmed term. */
  readonly onSearch: (term: string) => void;
  readonly initialValue?: string;
  readonly debounceMs?: number;
  readonly autoFocus?: boolean;
  readonly className?: string;
  readonly 'aria-label'?: string;
}

export function SearchField({
  placeholder = 'Tìm kiếm…',
  onSearch,
  initialValue = '',
  debounceMs = 300,
  autoFocus = false,
  className,
  'aria-label': ariaLabel = 'Tìm kiếm',
}: SearchFieldProps): React.JSX.Element {
  const [text, setText] = useState(initialValue);
  const onSearchRef = useRef(onSearch);
  onSearchRef.current = onSearch;
  const first = useRef(true);

  useEffect(() => {
    // Don't fire on mount: the list has already loaded its unfiltered page.
    if (first.current) {
      first.current = false;
      return undefined;
    }
    const timer = window.setTimeout(() => onSearchRef.current(text.trim()), debounceMs);
    return () => window.clearTimeout(timer);
  }, [debounceMs, text]);

  return (
    <div className={cn('relative flex items-center', className)}>
      <Input
        type="search"
        value={text}
        autoFocus={autoFocus}
        aria-label={ariaLabel}
        placeholder={placeholder}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            e.preventDefault();
            setText('');
          }
        }}
        prefix={<SearchGlyph />}
        className="pr-10"
      />
      {text !== '' ? (
        <IconButton
          label="Xóa từ khóa"
          size="sm"
          icon={<CloseGlyph />}
          onClick={() => setText('')}
          className="absolute right-1"
        />
      ) : null}
    </div>
  );
}

function SearchGlyph(): React.JSX.Element {
  return (
    <svg viewBox="0 0 20 20" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="1.8">
      <circle cx="9" cy="9" r="5.5" />
      <path d="m13.5 13.5 3.5 3.5" strokeLinecap="round" />
    </svg>
  );
}
